import { Avatar, Tooltip } from '@mui/material';
import { CellRender } from '../components/cell-render';
import { formatCnpj, formatNumber } from '../../utils/format.helper';
import { formatDate, getUtcDate, isDate } from '../../utils/date-helper';
import { TableMaskType } from '../core/table-creator';

interface ThousandMaskOptions {
  value: number;
  precision?: number;
  thousandPrecision?: number;
  prefix?: string;
  suffix?: string;
}

// Shared utility function for thousand mask formatting
const applyThousandMask = ({
  value,
  precision = 2,
  thousandPrecision = 2,
  prefix = '',
  suffix = '',
}: ThousandMaskOptions): string => {
  if (!value || isNaN(Number(value))) return '-';

  const absoluteNumber = Math.abs(value);
  let baseValue = value;
  let hasThousandMask = false;
  let unit = '';

  if (absoluteNumber >= 1e12) {
    hasThousandMask = true;
    baseValue = value / 1e12;
    unit = ' tri';
  } else if (absoluteNumber >= 1e9) {
    hasThousandMask = true;
    baseValue = value / 1e9;
    unit = ' bi';
  } else if (absoluteNumber >= 1e6) {
    hasThousandMask = true;
    baseValue = value / 1e6;
    unit = ' mi';
  } else if (absoluteNumber >= 1e3) {
    hasThousandMask = true;
    baseValue = value / 1e3;
    unit = ' mil';
  }

  const formattedValue = Intl.NumberFormat('pt-BR', {
    style: 'decimal',
    maximumFractionDigits: hasThousandMask ? thousandPrecision : precision,
    minimumFractionDigits: hasThousandMask ? thousandPrecision : precision,
  }).format(baseValue);

  return `${prefix}${formattedValue}${unit}${suffix}`;
};

/**
 * Gets the appropriate cell value based on its mask type
 */
export function getCellWithMask({
  value,
  meta = {},
  mask,
  currency = 'BRL',
  info,
}: {
  value: any;
  meta?: any;
  mask?: TableMaskType;
  currency?: string;
  info?: any;
}) {
  mask = mask === undefined ? meta?.mask : mask;
  const precision = meta?.precision || 2;
  const verifyPositiveNegative =
    meta?.showPositiveNegative || meta?.showNegative;

  switch (mask) {
    case TableMaskType.CNPJ:
      return formatCnpj(value);

    case TableMaskType.DATE:
      return isDate(value) ? formatDate(value) : value || '-';

    case TableMaskType.DATE_TIME:
      return isDate(value)
        ? getUtcDate(value, 'DD/MM/YY [as] HH:mm', true)
        : value || '-';

    case TableMaskType.DECIMAL:
      return handleDecimalMask(value, precision, verifyPositiveNegative, meta);

    case TableMaskType.INTEGER:
      return handleIntegerMask(value, verifyPositiveNegative, meta);

    case TableMaskType.PERCENT:
      return handlePercentMask(value, verifyPositiveNegative, meta, true);

    case TableMaskType.PERCENT_OTHER:
      return handlePercentMask(value, verifyPositiveNegative, meta, false);

    case TableMaskType.MONEY:
      return handleMoneyMask(
        value,
        currency,
        precision,
        verifyPositiveNegative,
        meta
      );

    case TableMaskType.BOOLEAN:
      return handleBooleanMask(value);

    case TableMaskType.USER:
      return handleUserMask(info);

    default:
      return value ?? '-';
  }
}

// Helper functions for each mask type
const handleDecimalMask = (
  value: any,
  precision: number,
  verifyPositiveNegative: boolean,
  meta: any
) => {
  if (!value || isNaN(Number(value))) return '-';

  const numberValue = Number(value);
  const formattedValue = meta?.thousandMask
    ? applyThousandMask({ value: numberValue, precision })
    : Intl.NumberFormat('pt-BR', {
        style: 'decimal',
        maximumFractionDigits: precision,
        minimumFractionDigits: 2,
        signDisplay: meta?.showSign ? 'always' : 'auto',
      }).format(value);

  if (verifyPositiveNegative) {
    return (
      <CellRender
        onlyNegative={meta?.showNegative}
        value={formattedValue}
        realValue={value}
      />
    );
  }

  return formattedValue;
};

const handleIntegerMask = (
  value: any,
  verifyPositiveNegative: boolean,
  meta: any
) => {
  if (value === null || value === undefined || isNaN(Number(value))) return '-';

  const numberValue = Number(value);
  const formattedValue = meta?.thousandMask
    ? applyThousandMask({
        value: numberValue,
        precision: 0,
        thousandPrecision: 2,
      })
    : formatNumber(value, { precision: 0 });

  if (verifyPositiveNegative) {
    return (
      <CellRender
        onlyNegative={meta?.showNegative}
        value={formattedValue}
        realValue={value}
      />
    );
  }

  return formattedValue;
};

const handlePercentMask = (
  value: any,
  verifyPositiveNegative: boolean,
  meta: any,
  multiply: boolean
) => {
  if (value === null || value === undefined || isNaN(Number(value))) return '-';

  const numberValue = Number(multiply ? value * 100 : value);
  const formattedValue = meta?.thousandMask
    ? applyThousandMask({ value: numberValue, precision: 2, suffix: '%' })
    : formatNumber(numberValue, { type: 'percent' });

  if (verifyPositiveNegative) {
    return (
      <CellRender
        onlyNegative={meta?.showNegative}
        value={formattedValue}
        realValue={value}
      />
    );
  }

  return formattedValue;
};

const handleMoneyMask = (
  value: any,
  currency: string,
  precision: number,
  verifyPositiveNegative: boolean,
  meta: any
) => {
  if (value === null || value === undefined || isNaN(Number(value))) return '-';

  const numberValue = Number(value);
  const minPrecision = meta?.minPrecision || 2;
  const maxPrecision =
    meta?.minPrecision > precision ? meta?.minPrecision : precision;

  const formattedValue = meta?.thousandMask
    ? applyThousandMask({
        value: numberValue,
        precision: maxPrecision,
        prefix: 'R$ ',
      })
    : Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: maxPrecision,
        minimumFractionDigits: minPrecision,
      }).format(numberValue);

  if (verifyPositiveNegative) {
    return (
      <CellRender
        onlyNegative={meta?.showNegative}
        value={formattedValue}
        realValue={value}
      />
    );
  }

  return formattedValue;
};

const handleBooleanMask = (value: any) => {
  if (value === null) return '-';
  if (typeof value !== 'boolean') return value;

  return Boolean(value) === true ? 'Sim' : 'Não';
};

// Define a list of avatar colors
const AVATAR_COLORS = [
  '#14AE5C', // green
  '#2A338D', // blue
  '#FF5F00', // orange
  '#AA0F5F', // magenta
  '#1976D2', // light blue
  '#FFC107', // amber
  '#9C27B0', // purple
  '#F44336', // red
  '#00796B', // teal
  '#607D8B', // blue grey
  '#E91E63', // pink
  '#FF5722', // deep orange
  '#3F51B5', // indigo
  '#009688', // green teal
  '#795548', // brown
  '#8BC34A', // light green
  '#FF9800', // orange
  '#CDDC39', // lime
  '#FFC107', // amber
];

/**
 * Simple hash function to get a consistent color for each email
 */
const getEmailColorIndex = (email: string): number => {
  if (!email) return 0;

  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Ensure positive number
  hash = Math.abs(hash);
  // Get index within the color array bounds
  return hash % AVATAR_COLORS.length;
};

export const handleUserMask = (info: any) => {
  const userName = info?.row?.original?.user_name || info.getValue() || '';
  const userEmail = info?.row?.original?.user_email || '';

  const getInitials = (name: string) => {
    if (!name) return '-';
    return name
      .split(' ')
      .map((part) => part[0]?.toUpperCase() || '')
      .filter((initial) => initial)
      .slice(0, 2)
      .join('');
  };

  const initials = getInitials(userName);
  const tooltipText =
    userName && userEmail
      ? `${userName} (${userEmail})`
      : userName || userEmail || '';

  // Get the color based on email
  const colorIndex = getEmailColorIndex(userEmail);
  const avatarColor = userEmail ? AVATAR_COLORS[colorIndex] : 'primary';

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Avatar
        sx={{
          margin: 'auto',
          width: 28,
          height: 28,
          fontWeight: 600,
          bgcolor: !initials ? 'primary' : avatarColor,
          fontSize: '11px',
        }}
      >
        {initials}
      </Avatar>
    </Tooltip>
  );
};
